import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Collapse } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import GroupsIcon from "@mui/icons-material/Groups";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSnackbar } from "notistack";
import moment from "moment";
import { openSessionRoom } from "../../../utils/sessionRoom";
import {
  get_availability_api, add_availability_api, delete_availability_api,
  get_tutoring_profile_api, get_instructor_bookings_api, respond_to_booking_api,
  get_consolidation_requests_api, claim_consolidation_request_api, release_consolidation_request_api,
} from "../../../DAL/SchoolPortal/Tutors";
import { get_earnings_detail_api } from "../../../DAL/Tutors/Tutors";
import {
  get_work_queue_api, get_group_sessions_api, get_school_students_api,
} from "../../../DAL/SchoolPortal/SchoolPortal";
import { useContentSetting } from "../../../Hooks/ContentSetting";
import { useSchool } from "../../../Hooks/SchoolContext";
import { regionOf, currencySymbol } from "../../../utils/region";
import WeekAvailabilityCalendar, { EVENT_KINDS, DEFAULT_WORKING_HOURS } from "./WeekAvailabilityCalendar";
import MonthAvailabilityCalendar from "./MonthAvailabilityCalendar";
import SessionPrep from "./SessionPrep";
import SessionDetail from "./SessionDetail";
import BookingActions from "./BookingActions";
import RecordActionPanel from "../StudentRecord/RecordActionPanel";
import GroupSessions from "../GroupSessions";
import Sessions from "./Sessions";
import SessionComposer from "./SessionComposer";
import InstructorTutorsView from "./InstructorTutorsView";
import { describeRule } from "./InstructorPay";
import {
  surface, border, textPrimary, textSecondary, textMuted, accent, font,
  warning, error as errorColor, statTileSx, filledButtonSx, ACTION_COLORS,
  primaryButtonSx, secondaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * TEACHING — the diary, and the strips waiting to go on it.
 *
 * WHY THE CALENDAR IS THE PAGE
 * An instructor's work is a shape in time. A sectioned list answers "what is
 * outstanding?" correctly and cannot answer "can I take this?" at all — the
 * question every incoming request actually asks. Five rows in a list and five
 * blocks in a diary carry identical data and completely different information:
 * only the diary shows the gap on Wednesday, that Thursday is already gone,
 * and that the request that just arrived lands on top of a class.
 *
 * IT IS THE AVAILABILITY GRID, NOT A SECOND CALENDAR
 * WeekAvailabilityCalendar already draws working hours, closed time and
 * reserved time, and lets an instructor drag new ones out. A teaching calendar
 * that showed only lessons would have been the third calendar in this codebase
 * and the only one that could not answer "when am I free?" — which is half of
 * "can I take this?". So the lessons were added to the availability grid
 * instead of the availability layer being rebuilt behind a lesson grid.
 *
 * That also closes a real gap: until now the only way to close off an
 * afternoon was a different page. Blocking out next Friday is work, and work
 * belongs on the diary; the working-hours PATTERN is setup, and lives in the
 * section at the foot of this page.
 *
 * BOOKINGS ARE DRAWN FROM BOOKINGS, NOT FROM THEIR AVAILABILITY SHADOW
 * Confirming a lesson also writes an InstructorAvailability row, so a booked
 * lesson exists twice. The availability row knows only that the time is taken;
 * the booking knows the student, the subject, the lesson number and whether
 * the debrief is still owed. So the shadow rows are filtered out by
 * `booking_id` and the real bookings are drawn — otherwise every lesson
 * renders twice, once anonymously.
 *
 * GROUP CLASSES ARE A SECTION, NOT A SECOND PAGE
 * A class and a 1:1 are both a thing you turn up and teach, and they were two
 * nav items for one person's job. They are already drawn on the calendar
 * above; what lives below is the part a calendar cannot do — seats, prices and
 * calling one off. The list is scoped to the viewer: an instructor sees their
 * own, because the endpoint is school-wide and an unscoped list let a CFI
 * cancel a colleague's evening and refund eight seats that were not theirs.
 *
 * THE FRONT DESK LANDS HERE TOO, and that is why the classes list is not
 * hidden behind `canTeach`. A receptionist does not teach, but calling off
 * Thursday's class is their job — hiding it is how eight students once held a
 * booking for a class that was not happening. They get the classes and none of
 * the diary: one nav label, one page, contents by role.
 *
 * AVAILABILITY & RATES IS A SECTION TOO, FOR THE SAME REASON
 * It was a page of its own, and it rendered the same calendar with the same
 * column of bookings beside it — two pages showing one week, and the one
 * WITHOUT the lessons drawn on it was the worse of the two. What it uniquely
 * held is setup: which days you work, what you cover, what you charge, how you
 * get paid. That is now a section at the foot of this page, collapsed, because
 * it is opened once or twice a year while the diary above it is opened daily.
 *
 * Collapsed is the whole argument. The original page put Today and Profile in
 * the same tab bar, which asserts they are equally likely; a section that
 * starts shut says the true thing — this is here when you want it, and you
 * will rarely want it.
 *
 * THE BAY IS NOT A SIDEBAR OF EXTRAS
 * A calendar shows time, and a calendar cannot show a BLOCKED student — the
 * whole point of that state is that no session exists yet, so there is nothing
 * to draw. Anything with no time on it has no home on a diary, which is what a
 * strip bay is for: work not yet in the diary, held beside it until it is.
 * Accept a request and the strip becomes a block; the two halves of this page
 * are the two halves of that gesture.
 */

const H = { fontFamily: font };
const bayLabel = {
  ...H, fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.09em",
  textTransform: "uppercase", color: textSecondary,
};

/**
 * A ledger row, as the booking SessionPrep expects.
 *
 * One function because both the brief and the debrief open the same dialog
 * from the same rows, and two copies of a field rename is how one of them
 * quietly stops passing `end_time`.
 */
const asBooking = (row) => ({
  ...row, _id: row.id, studentName: row.student,
  start_time: row.startedAt, end_time: row.endedAt,
});

const money = (n, c = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: c, maximumFractionDigits: 0 }).format(n || 0);

const hoursMins = (mins) => {
  if (!mins) return "0h";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/**
 * A flight strip.
 *
 * The shape is borrowed on purpose: fixed fields in fixed places, times in
 * tabular figures, a coloured spine for state, and the decision separated from
 * the data by a rule. A controller reads a bay by position rather than by
 * re-reading each strip, and that only works if every strip puts the same
 * thing in the same place — so time is always top-left and the actions are
 * always the last band, whatever the strip is for.
 */
function Strip({ kind, when, dur, title, sub, meta, actions, onOpen }) {
  const k = EVENT_KINDS[kind] || EVENT_KINDS.confirmed;
  return (
    <Box
      onClick={onOpen}
      sx={{
        display: "flex", mb: 0.75, backgroundColor: surface,
        border: `1px solid ${border}`, borderRadius: "8px", overflow: "hidden",
        // The whole strip opens it. Hiding the only way in behind a small
        // button means the obvious gesture — click the thing — does nothing.
        cursor: onOpen ? "pointer" : "default",
        "&:hover": onOpen ? { borderColor: k.color } : {},
      }}
    >
      <Box sx={{ width: 4, flexShrink: 0, backgroundColor: k.color }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ px: 1, pt: 0.8, pb: actions ? 0.7 : 0.8 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.7 }}>
            {when && (
              <Typography sx={{
                ...H, fontSize: "11.5px", fontWeight: 800, color: textPrimary,
                fontVariantNumeric: "tabular-nums", flexShrink: 0,
              }}>
                {when}
              </Typography>
            )}
            {dur && <Typography sx={{ ...H, fontSize: "10.5px", color: textMuted, flexShrink: 0 }}>{dur}</Typography>}
            {meta && (
              <Typography noWrap sx={{ ...H, fontSize: "10.5px", color: warning, ml: "auto", fontWeight: 700 }}>
                {meta}
              </Typography>
            )}
          </Box>
          <Typography noWrap sx={{ ...H, fontSize: "13px", fontWeight: 700, color: textPrimary, mt: 0.15 }}>
            {title}
          </Typography>
          {sub && (
            <Typography sx={{ ...H, fontSize: "11px", color: textSecondary, mt: 0.15, lineHeight: 1.4 }}>
              {sub}
            </Typography>
          )}
        </Box>
        {actions && (
          <Box
            onClick={(ev) => ev.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.7, borderTop: `1px solid ${border}` }}
          >
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function Bay({ label, count, children }) {
  const kids = React.Children.toArray(children).filter(Boolean);
  if (!kids.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.7, mb: 0.8 }}>
        <Typography sx={bayLabel}>{label}</Typography>
        {count > 0 && <Typography sx={{ ...bayLabel, color: accent }}>{count}</Typography>}
      </Box>
      {kids}
    </Box>
  );
}

/** Above this the bay stops being a bay and becomes a backlog. */
const MAX_OWED = 6;

/**
 * What the one button on a strip should say and do.
 *
 * IT USED TO SAY "PREP" ON EVERY ROW. A verb with no object, identical
 * whether the session is in three days or three minutes, and giving no hint
 * that behind it sit the student's weakest ACS areas, what happened last
 * time, their open tasks and their own note. "Open brief" at least names what
 * you get; when there is something more urgent than reading, the button is
 * that instead.
 *
 * ONE PRIMARY ACTION, NOT THREE CONTROLS. A strip used to carry Prep, then
 * sometimes Join, then an overflow menu, none of them clearly first. There is
 * only ever one thing you most likely came to do, and which one it is depends
 * on the clock.
 *
 * THE CLOCK IS THE SERVER'S. `join` is computed by the same
 * utils/session_join_window.js the video endpoint enforces with, so this can
 * never offer a button the server would refuse — or, as it did, hide one it
 * would have allowed.
 */
function stripAction(e) {
  if (e.klass) return { label: "Open class", icon: "class", sx: filled(ACTION_COLORS.klass) };
  const b = e.booking;
  if (!b) return null;
  // Amber, because that is what an owed debrief looks like everywhere else.
  if (b.needsDebrief) return { label: "Write the debrief", icon: null, sx: filled(ACTION_COLORS.debrief) };
  // Green, and nothing else on this page is: it is the only action with a
  // clock running on it.
  if (b.join?.applicable && b.join.open) return { label: "Join now", icon: "video", sx: filled(ACTION_COLORS.join) };
  // Reading is not urgent and should not look it. An outline among filled
  // buttons is the difference between "you could" and "you should".
  return { label: "Open brief", icon: null, sx: tinySecondary };
}

/**
 * "Join opens 12:30" — said only when it is nearly true.
 *
 * A countdown on a lesson three weeks away is noise, and "too early" with no
 * time attached sends someone back to their email to work out the answer.
 */
function joinHint(e) {
  const j = e.booking?.join;
  if (!j?.applicable || j.state !== "too_early") return null;
  const opens = new Date(j.opensAt).getTime();
  if (opens - Date.now() > 2 * 60 * 60 * 1000) return null;
  return `Join opens ${moment(j.opensAt).format("HH:mm")}`;
}

const tinyPrimary = { ...primaryButtonSx, flex: 1, minHeight: 0, py: 0.45, px: 1, fontSize: "11.5px" };
const tinySecondary = { ...secondaryButtonSx, flex: 1, minHeight: 0, py: 0.45, px: 1, fontSize: "11.5px" };

/**
 * The bay's buttons are narrower than the rest of the product's, so they
 * stretch — but the COLOUR comes from the shared vocabulary, not from a
 * second copy of it. An encoding with two implementations is two encodings.
 */
const filled = (color) => ({ ...filledButtonSx(color), flex: 1, px: 1, fontSize: "11.5px", py: 0.45 });

export default function Teaching({ teaches = true, canManageAllClasses = false }) {
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo } = useContentSetting();
  const { school } = useSchool();
  const myId = userInfo?._id ? String(userInfo._id) : null;
  const region = regionOf(school);
  // Group classes are a US Part 61 concept — four discussion-shaped lessons in
  // that syllabus. The student side already 404s for UK accounts, so offering a
  // UK school the scheduler let an instructor create a class nobody could join.
  // US only until there is a UK course for them to be part of.
  const groupClasses = region === "US";

  const [state, setState] = useState({ loading: true, partial: null });
  const [bookings, setBookings] = useState([]);
  const [queue, setQueue] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [classes, setClasses] = useState([]);
  const [slots, setSlots] = useState([]);
  const [profile, setProfile] = useState(null);
  const [students, setStudents] = useState([]);
  // The dragged range the composer is open on, and the syllabus it offers.
  const [compose, setCompose] = useState(null);
  // The consolidation pool: what I can pick up, and what I have picked up and
  // still owe a time. `answering` is the request the composer is open for.
  const [pool, setPool] = useState({ claimable: [], mine: [], claimHours: 24 });
  const [answering, setAnswering] = useState(null);
  const [syllabus, setSyllabus] = useState({ lessons: [], defaultPricePerHead: 25 });
  const gridWrapRef = useRef(null);
  const [gridMaxHeight, setGridMaxHeight] = useState(560);
  const [busyId, setBusyId] = useState(null);
  const [prep, setPrep] = useState(null);
  // The panel: a glance at a session without losing the week behind it.
  const [peekId, setPeekId] = useState(null);
  const [propose, setPropose] = useState(null);
  const navigate = useNavigate();
  const classesRef = useRef(null);
  const setupRef = useRef(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [classesOpen, setClassesOpen] = useState(false);
  /**
   * TABS, BECAUSE THE CALENDAR IS TALLER THAN THE SCREEN.
   *
   * Everything used to sit in one column: diary, then classes, then setup.
   * The argument for that was honest — a section that starts shut says "you
   * will rarely want this" better than a tab that claims equal billing — but
   * it assumed the reader could SEE the sections to know they were there. The
   * week grid is a full viewport on its own, so in practice nothing below it
   * was ever reached, and a section nobody scrolls to is worse than a tab
   * nobody clicks: at least the tab is visible.
   *
   * SESSIONS LANDS, NOT THE DIARY. The calendar answers "when am I free",
   * which is a question asked while setting availability or while somebody is
   * on the phone. The question an instructor opens this page with is "what am
   * I teaching, and is anything waiting on me" — and that is a list, with a
   * name, a time and a status on every row. The week grid is also a full
   * viewport on its own, so landing on it pushed the actual answer below the
   * fold.
   */
  const [tab, setTab] = useState("sessions");
  const [pay, setPay] = useState(null);
  // Week edits, month scans. The week grid is for dragging time out; it is the
  // wrong tool for "am I free the week of the 20th", which is what an
  // instructor is actually asked on the phone.
  const [calendarView, setCalendarView] = useState("week");
  const [jumpTo, setJumpTo] = useState(null);

  /** All in parallel, each allowed to fail on its own. */
  const load = useCallback(async () => {
    // A receptionist has no diary, no bay and no earnings. Firing the six
    // instructor endpoints at them would 403 six times and the page would
    // report "6 of 7 panels could not load" — an error message for behaving
    // exactly as designed. GroupSessions fetches its own list.
    if (!teaches) { setState({ loading: false, partial: null }); return; }
    const settled = await Promise.allSettled([
      get_instructor_bookings_api(),
      get_work_queue_api({ scope: "mine" }),
      get_earnings_detail_api({ grain: "week" }),
      get_group_sessions_api(),
      get_availability_api(),
      get_tutoring_profile_api(),
      get_school_students_api(),
      get_consolidation_requests_api(),
    ]);
    const [b, q, e, g, a, p, st, cr] = settled.map((s) => (s.status === "fulfilled" ? s.value : null));
    if (b?.code === 200) setBookings(b.data || []);
    if (q?.code === 200) setQueue(q);
    if (e?.code === 200) setEarnings(e.data || e);
    if (g?.code === 200) {
      setClasses(g.sessions || []);
      // Lessons come from the server rather than being transcribed here, so a
      // lesson renamed in the syllabus does not need a second edit in this repo
      // to stop being wrong. See controllers/school/group_sessions.js.
      if (g.lessons?.length) {
        setSyllabus({ lessons: g.lessons, defaultPricePerHead: g.defaultPricePerHead || 25 });
      }
    }
    if (a?.code === 200) setSlots(a.data || []);
    if (p?.code === 200) { setProfile(p.data); setPay(p.compensation || null); }
    if (st?.code === 200) setStudents(st.data || st.students || []);
    if (cr?.code === 200) {
      setPool({ claimable: cr.claimable || [], mine: cr.mine || [], claimHours: cr.claimHours || 24 });
    }
    const failed = settled.filter((s) => s.status !== "fulfilled" || s.value?.code !== 200).length;
    setState({
      loading: false,
      partial: failed ? `${failed} of ${settled.length} panels could not load. The rest is current.` : null,
    });
  }, [teaches]);

  useEffect(() => { load(); }, [load]);

  /**
   * THE DIARY FITS THE WINDOW. NOTHING ON IT SCROLLS THE PAGE.
   *
   * The week grid is the only thing here allowed to be taller than the space
   * it has, and it has its own scroller for that. So the grid is told how much
   * room is left rather than picking a fixed 560px and letting the page grow
   * past the fold underneath it.
   *
   * Measured, not assumed: the banners above the diary (failed payout, closed
   * to bookings, partial load) appear and disappear, and a hardcoded offset
   * would push the last hour off-screen exactly when one of them is showing.
   */
  useEffect(() => {
    if (tab !== "diary") return undefined;
    const measure = () => {
      const el = gridWrapRef.current;
      if (!el) return;
      // Below `lg` the diary is not pinned (see the root Box), so the grid goes
      // back to a fixed height and the page scrolls the way it always did.
      if (window.innerWidth < 1200) { setGridMaxHeight(560); return; }
      // Everything the calendar draws below its own scroller — the week
      // toolbar, the day header row, and the legend under the grid.
      const CALENDAR_CHROME = 150;
      // Deliberate slack under the card. A grid that ends exactly on the fold
      // reads as cut off even when nothing is missing, and there is nowhere for
      // the eye to rest — so the last hour stops short of the window edge.
      const BREATHING_ROOM = 44;
      const top = el.getBoundingClientRect().top;
      setGridMaxHeight(Math.max(
        240,
        Math.round(window.innerHeight - top - CALENDAR_CHROME - BREATHING_ROOM),
      ));
    };
    measure();
    // One deferred pass: the banners above mount in the same frame, and the
    // first measurement would otherwise be taken against the page without them.
    const t = setTimeout(measure, 60);
    window.addEventListener("resize", measure);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
    // `failedOut` is deliberately NOT a dependency: it is declared further down
    // this component, and naming it here reads it in its temporal dead zone.
    // The deferred pass above already catches its banner.
  }, [tab, calendarView, state.loading, state.partial]);

  const now = Date.now();

  /**
   * MY classes, not the school's.
   *
   * /api/school/group-sessions is school-scoped by design — the front desk has
   * to see every class to call one off. Rendering it unfiltered on a page
   * called Teaching put every other instructor's Thursday evening in this
   * instructor's diary. Falls back to the unfiltered list when there is no id
   * to compare against (god-mode impersonation), so the failure mode is the
   * old behaviour rather than a silently empty calendar.
   */
  const myClasses = useMemo(
    () => classes.filter((c) => !myId || String(c.instructorId) === myId),
    [classes, myId]
  );

  /**
   * Everything with a person attached, in one shape.
   *
   * `cancelled` is deliberately absent: a cancelled session is not a claim on
   * anyone's time, and drawing it makes a free Tuesday look busy — the exact
   * lie this page exists to stop telling.
   */
  const events = useMemo(() => {
    const out = [];
    for (const b of bookings) {
      let kind = null;
      if (b.status === "pending_instructor_approval") kind = "requested";
      else if (b.status === "pending_student_confirmation") kind = "proposed";
      else if (["confirmed", "completed"].includes(b.status)) {
        kind = new Date(b.end_time).getTime() > now ? "confirmed" : (b.needsDebrief ? "owed" : "done");
      }
      if (!kind) continue;
      out.push({
        id: `b:${b._id}`,
        kind,
        start: new Date(b.start_time),
        end: new Date(b.end_time),
        title: b.studentName,
        sub: [b.subject, b.lesson ? `Lesson ${b.lesson}` : null].filter(Boolean).join(" · "),
        booking: b,
      });
    }
    for (const c of myClasses) {
      if (c.status !== "scheduled") continue;
      out.push({
        id: `c:${c.id}`,
        kind: "class",
        start: new Date(c.at),
        end: new Date(c.endsAt || new Date(c.at).getTime() + 3600000),
        title: c.title,
        sub: `${c.going} of ${c.capacity} booked`,
        klass: c,
      });
    }
    return out;
  }, [bookings, myClasses, now]);

  /**
   * The availability layer only — closed time and reserved time.
   *
   * A `visibility: "booking"` row is the SHADOW of a lesson: request_booking
   * creates one to claim the range against a race, and `events` already draws
   * that lesson properly, with the student's name on it. Drawing both puts two
   * blocks on one hour and the second is anonymous.
   *
   * FILTERED ON VISIBILITY, NOT ON booking_id. The obvious-looking check is
   * `!s.booking_id`, and it would have let all 270 shadow rows in production
   * straight through — because only request_booking sets that field, and none
   * of the rows that exist today have it. `visibility` is required by the
   * schema, so it is the field that is actually always there.
   */
  const exceptions = useMemo(
    () => slots.filter((s) => s.visibility !== "booking" && !s.booking_id),
    [slots]
  );

  /**
   * All-time, and only from sessions that have actually ENDED.
   *
   * Counting a confirmed future booking is how a dashboard starts lying to the
   * person it is meant to reassure: the number would FALL when the session was
   * taught and the payout figure caught up with it.
   */
  const stats = useMemo(() => {
    let revenue = 0;
    let minutes = 0;
    let sessions = 0;
    for (const b of bookings) {
      if (!["confirmed", "completed"].includes(b.status)) continue;
      if (new Date(b.end_time).getTime() > now) continue;
      sessions += 1;
      minutes += Math.max(0, Math.round((new Date(b.end_time) - new Date(b.start_time)) / 60000));
      if (b.payment_status === "paid") revenue += b.payout_amount || b.price || 0;
    }
    return { revenue, minutes, sessions };
  }, [bookings, now]);

  const requests = useMemo(
    () => bookings
      .filter((b) => b.status === "pending_instructor_approval")
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [bookings]
  );

  const blocked = useMemo(
    () => (queue?.queue || []).filter((r) => r.reasons?.some((x) => x.reason === "awaiting_session")),
    [queue]
  );

  /**
   * Oldest first, because the sort order is a claim about priority and the
   * oldest un-written-up session is the student who has been locked out of
   * their next lesson the longest.
   */
  const owed = useMemo(
    () => bookings.filter((b) => b.needsDebrief).sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [bookings]
  );

  const awaitingReply = useMemo(
    () => bookings
      .filter((b) => b.status === "pending_student_confirmation" && new Date(b.start_time).getTime() > now)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
    [bookings, now]
  );

  /**
   * What has already been taught, newest first — the opposite order to
   * everything in the bay, because the bay is a queue and this is a record.
   * Derived from the bookings already loaded rather than a seventh request:
   * "past" is a filter on data this page has, not a different question.
   */

  const nextUp = useMemo(
    () => events
      .filter((e) => (e.kind === "confirmed" || e.kind === "class") && e.start.getTime() > now)
      .sort((a, b) => a.start - b.start)
      .slice(0, 5),
    [events, now]
  );

  const respond = async (id, decision) => {
    setBusyId(id);
    try {
      const r = await respond_to_booking_api(id, decision);
      enqueueSnackbar(r.message || (decision === "accept" ? "Accepted" : "Declined"), {
        variant: r.code === 200 ? "success" : "error",
      });
      if (r.code === 200) load();
    } finally { setBusyId(null); }
  };

  const createSlot = async (start, end, visibility, reservedStudent) => {
    const slot = { start_time: start.toISOString(), end_time: end.toISOString(), visibility };
    if (visibility === "reserved" && reservedStudent) {
      slot.reserved_for_student = reservedStudent._id;
      slot.reserved_for_student_type = "flight_school_student";
    }
    const res = await add_availability_api({ slots: [slot] });
    if (res.code === 200) {
      setSlots((prev) => [...prev, ...res.data].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
    } else {
      enqueueSnackbar(res.message || "Could not change your availability", { variant: "error" });
    }
  };

  /**
   * Take a consolidation request out of the pool.
   *
   * Losing the race is a 409 and is reported as news, not as an error: someone
   * else getting there first is the pool working, and a red failure toast for
   * it would teach instructors not to press the button.
   */
  const claimRequest = async (id) => {
    setBusyId(id);
    try {
      const res = await claim_consolidation_request_api(id);
      enqueueSnackbar(res.message || (res.code === 200 ? "Picked up." : "Somebody got there first."), {
        variant: res.code === 200 ? "success" : "info",
      });
      await load();
    } finally { setBusyId(null); }
  };

  const releaseRequest = async (id) => {
    setBusyId(id);
    try {
      const res = await release_consolidation_request_api(id);
      enqueueSnackbar(res.message || "Back in the pool.", { variant: "info" });
      await load();
    } finally { setBusyId(null); }
  };

  /**
   * Put a time to one I have picked up.
   *
   * Opens the composer on the student's FIRST stated window rather than on now:
   * they already said when they are free, and ignoring that to make them
   * negotiate again is the thing the pool exists to avoid. An hour inside the
   * window, because the window is a band, not an appointment.
   */
  const answerRequest = (req) => {
    const w = (req.windows || [])[0];
    const start = w ? new Date(w.start) : (() => {
      const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); return d;
    })();
    const end = new Date(start.getTime() + 60 * 60000);
    setAnswering(req);
    setCompose({ start, end });
  };

  const deleteSlot = async (id) => {
    const res = await delete_availability_api(id);
    if (res.code === 200) setSlots((prev) => prev.filter((s) => s._id !== id));
    else enqueueSnackbar(res.message || "Could not remove that block", { variant: "error" });
  };

  // This used to scroll down the page to the classes section. It switches tabs
  // now. The scroll to top matters: arriving at a tab already scrolled down by
  // the diary's height reads as a blank page.


  /**
   * Opening a session goes to the session, not to a panel over the diary.
   *
   * A class and a 1:1 are the same navigation: /school/sessions/:id handles
   * both and the server decides which from the id. Sending classes somewhere
   * else would mean the one thing a person does most on this page — open the
   * thing they clicked — behaved differently depending on what it was.
   */
  const openSession = (id) => navigate(`/school/sessions/${id}`);

  /**
   * A CALENDAR BLOCK OPENS THE PANEL; THE BAY OPENS THE PAGE.
   *
   * The two are the same screen at two sizes, and the difference is what you
   * were doing when you clicked. Someone scanning a week and tapping Thursday
   * is asking a question about Thursday and wants the week still behind them;
   * someone picking a session out of a list has already stopped scanning. A
   * full navigation for a glance loses the scroll position that made the
   * glance possible.
   *
   * Nobody is stuck with the smaller one — the panel expands into the page.
   */
  const pick = (e) => setPeekId(e.klass ? e.klass.id : e.booking._id);

  if (state.loading) {
    return <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress size={22} sx={{ color: accent }} /></Box>;
  }

  const cur = earnings?.currency || bookings[0]?.currency || "USD";
  const failedOut = earnings?.totals?.failed || 0;
  const clearing = earnings?.totals?.clearing || 0;
  const nothingWaiting = !requests.length && !blocked.length && !owed.length && !awaitingReply.length;

  const tiles = [
    { label: "Earned", value: money(stats.revenue, cur) },
    { label: "Time taught", value: hoursMins(stats.minutes) },
    { label: "Sessions", value: stats.sessions },
    { label: "Needs your answer", value: requests.length, tone: requests.length ? accent : undefined },
  ];

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 2 }, maxWidth: 1400, mx: "auto",
      // The diary is pinned to the window: the week grid has its own scroller
      // and is sized to whatever is left, so the page itself never scrolls.
      // The other tabs are lists that are legitimately longer than a screen,
      // and pinning those would just move the scrollbar somewhere stranger.
      ...(tab === "diary" ? {
        height: { xs: "auto", lg: "100vh" },
        display: { xs: "block", lg: "flex" },
        flexDirection: "column",
        overflow: { xs: "visible", lg: "hidden" },
        pb: 3,
      } : {}),
    }}>
      {/* Container padding, title size and tracking are SchoolStudents.jsx's,
          so the two pages start at the same place. Matching a sibling beats
          picking a nice number: a portal where every page sets its own top
          margin reads as several products.

          THE STATS SIT IN THE TITLE ROW, not in a band of their own.
          The row was a title against 1,100px of nothing, and directly beneath
          it four cards did the job that empty half could have done. Together
          they pushed the first hour of the diary most of a screen down — on a
          page whose entire argument is that you can see your week.
          They are also OUTSIDE the tab switch, so the top of the page does not
          jump by a hundred pixels every time you change tab. */}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.25, mb: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ mr: "auto", minWidth: 0 }}>
          <Typography sx={{
            ...H, fontSize: "20px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.2px",
          }}>
            Teaching
          </Typography>
          <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.2 }}>
            {teaches ? moment().format("dddd D MMMM") : "Group classes at your school — schedule them, fill them, call them off"}
          </Typography>
        </Box>

        {/* The tiles are ONE group, not four loose items in the title row.
            Left as siblings of the heading they each wrapped independently, so
            a narrow window peeled them off one at a time and left a ragged
            second line under a title that still had space beside it. Grouped,
            they move as a block and stay together.

            Bottom-aligned, not centre-aligned: the heading beside them is two
            lines, so centring floated them halfway up it against nothing. The
            baseline they now share is the subtitle's. */}
        {teaches && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {tiles.map((t) => (
              <Box key={t.label} sx={{
                px: 1.5, py: 0.8, minWidth: 104, flex: "1 1 auto",
                backgroundColor: surface, border: `1px solid ${border}`, borderRadius: "11px",
                // Same seating as the calendar card, so the top of the page and
                // the grid under it read as the same material.
                boxShadow: "0 1px 2px rgba(26,26,26,0.05)",
              }}>
                <Typography sx={{
                  ...H, fontSize: "17px", fontWeight: 800, lineHeight: 1.15,
                  color: t.tone || textPrimary, fontVariantNumeric: "tabular-nums",
                }}>
                  {t.value}
                </Typography>
                <Typography sx={{ ...H, fontSize: "10.5px", color: textSecondary, mt: 0.1, whiteSpace: "nowrap" }}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* A FAILED payout means money did not reach them, so it stays loud. What
          it does NOT do any more is print the provider's raw sentence: Stripe's
          message carries the PLATFORM's key prefix and a dashboard link for the
          platform's own account, and it rendered as an unbounded block of red
          running off the top-right of the page. None of that is an
          instructor's to read or act on. The amount is the alert; the
          diagnosis belongs on Payouts, where whoever can fix it is looking. */}
      {failedOut > 0 ? (
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          mb: 1.5, px: 1.5, py: 0.9, borderRadius: "10px",
          backgroundColor: `${errorColor}0d`, border: `1px solid ${errorColor}33`,
        }}>
          <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 700, color: errorColor }}>
            {money(failedOut, cur)} could not be paid out
          </Typography>
          {/* "Your payout account needs attention" is the WRONG sentence when
              the failure is ours — it sends an instructor to re-verify a Stripe
              account that is working perfectly. The server says whose problem
              it is; this only has to read it. */}
          <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary }}>
            {earnings?.needsAttention?.[0]?.actor === "platform"
              ? "Nothing for you to do — the problem is on XAir's side and we have been notified."
              : "Your payout account needs attention."}
          </Typography>
          {teaches && earnings?.needsAttention?.[0]?.actor !== "platform" && (
            <Box
              onClick={() => setTab("payouts")}
              sx={{
                ...H, fontSize: "12.5px", fontWeight: 700, color: errorColor,
                cursor: "pointer", textDecoration: "underline", ml: "auto",
              }}
            >
              See Payouts
            </Box>
          )}
        </Box>
      ) : clearing > 0 ? (
        <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mb: 1.5 }}>
          {money(clearing, cur)} clearing
        </Typography>
      ) : null}

      {/* One row, above everything, so what is on this page is legible without
          scrolling a calendar's height to find out. A receptionist gets no bar
          — they have exactly one section and a tab bar of one is furniture. */}
      {teaches && (
        <Box sx={{ display: "flex", gap: 0.3, mb: 1.75, borderBottom: `1px solid ${border}`, overflowX: "auto" }}>
          {[
            // Sessions leads: it is the daily question. Classes and History
            // were two tabs for one fact — a class and a 1:1 are both a thing
            // you turn up and teach, and neither tab could answer a question
            // about the other.
            ["sessions", "Sessions"],
            // The diary is for setting availability and answering "am I free
            // on the 20th", which is a weekly job rather than a daily one.
            ["diary", "Diary"],
            ["payouts", "Payouts"],
            ["profile", "Profile & rates"],
          ].map(([k, lbl]) => (
            <Box
              key={k}
              onClick={() => { setTab(k); if (k === "profile") setSetupOpen(true); }}
              sx={{
                px: 1.6, py: 1.05, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                fontFamily: font, fontSize: "13px", fontWeight: 700,
                color: tab === k ? accent : textSecondary,
                borderBottom: `2.5px solid ${tab === k ? accent : "transparent"}`,
                mb: "-1px",
                "&:hover": { color: tab === k ? accent : textPrimary },
              }}
            >
              {lbl}
            </Box>
          ))}
          {/* NOT A TAB. A tab bar asserts its items are equally likely, and
              this is a bench you visit when something is wrong with the call —
              not a fifth thing an instructor does. It rides at the end of the
              row so it is findable without claiming to be daily work. */}
          <Box sx={{ flex: 1, minWidth: 8 }} />
          <Box
            onClick={() => navigate("/school/teaching/video-test")}
            sx={{
              display: "flex", alignItems: "center", gap: 0.5, alignSelf: "center",
              px: 1.2, py: 0.5, mb: 0.5, borderRadius: "999px", cursor: "pointer",
              whiteSpace: "nowrap", border: `1px solid ${border}`,
              fontFamily: font, fontSize: "11.5px", fontWeight: 600, color: textSecondary,
              "&:hover": { color: textPrimary, borderColor: textMuted },
            }}
          >
            <VideocamIcon sx={{ fontSize: 14 }} />
            Test video
          </Box>
        </Box>
      )}


      {state.partial && (
        <Typography sx={{ ...H, fontSize: "12.5px", color: warning, mb: 1.5 }}>{state.partial}</Typography>
      )}

      {profile && !profile.tutoring?.isBookable && (
        <Typography sx={{ ...H, fontSize: "12.5px", color: warning, fontWeight: 600, mb: 1.5 }}>
          You are closed to new bookings. Lessons already confirmed are not affected.
        </Typography>
      )}

      {teaches && tab === "diary" && (
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
        gap: 2,
        flex: 1, minHeight: 0,
      }}>
        <Box ref={gridWrapRef} sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: "flex", gap: 0.3, backgroundColor: "rgba(0,0,0,0.035)", borderRadius: "9px", p: 0.3, width: "fit-content" }}>
            {["week", "month"].map((v) => (
              <Button
                key={v}
                onClick={() => setCalendarView(v)}
                sx={{
                  minHeight: 0, py: 0.45, px: 1.4, fontFamily: font, fontSize: "11.5px", fontWeight: 700,
                  textTransform: "none", borderRadius: "7px",
                  color: calendarView === v ? "#fff" : textSecondary,
                  backgroundColor: calendarView === v ? textPrimary : "transparent",
                  "&:hover": { backgroundColor: calendarView === v ? textPrimary : "rgba(0,0,0,0.05)" },
                }}
              >
                {v === "week" ? "Week" : "Month"}
              </Button>
            ))}
          </Box>
          </Box>

          {calendarView === "week" ? (
            <WeekAvailabilityCalendar
              schoolRegion={region}
              slots={exceptions}
              events={events}
              workingHours={profile?.tutoring?.workingHours || DEFAULT_WORKING_HOURS}
              closed={Boolean(profile) && !profile.tutoring?.isBookable}
              onCompose={(r) => setCompose(r)}
              composing={Boolean(compose)}
              gridMaxHeight={gridMaxHeight}
              onDeleteSlot={deleteSlot}
              onPickEvent={pick}
              jumpTo={jumpTo}
            />
          ) : (
            <MonthAvailabilityCalendar
              // Every event, not just confirmed bookings — a month that omits
              // the class you teach on Thursdays reports the month as quieter
              // than it is.
              bookings={events.map((e) => ({ start_time: e.start, end_time: e.end }))}
              blocks={exceptions.filter((s) => s.visibility === "blocked")}
              region={region}
              onSelectDay={(d) => { setJumpTo(new Date(d)); setCalendarView("week"); }}
            />
          )}
        </Box>

        {/* ── the bay ──────────────────────────────────────────────────────
            What is waiting on you and what is coming up, beside the diary
            rather than over it. It scrolls ON ITS OWN — the page does not — so
            a long queue lengthens this column instead of pushing the calendar
            off the bottom of the window. */}
        <Box sx={{
          display: { xs: "block", lg: "flex" }, flexDirection: "column",
          minHeight: 0, overflowY: "auto", pr: 0.5,
        }}>

          {/* ── the consolidation pool ────────────────────────────────────
              First in the bay, above even booking requests. A student here is
              STOPPED: they have finished a lesson, the course will not open the
              next one, and nobody has taken them yet. Every other strip below
              is someone who can still make progress today. */}
          <Bay label="Waiting for an instructor" count={pool.claimable.length}>
            {pool.claimable.map((r) => (
              <Strip
                key={r.id}
                kind="requested"
                when={`Lesson ${r.lesson}`}
                dur={r.waitingDays === 0 ? "today" : `${r.waitingDays}d waiting`}
                meta={r.pool === "marketplace" ? "outside your school" : null}
                title={r.student}
                sub={[
                  r.lessonTitle,
                  r.windows?.length ? `free at ${r.windows.length} time${r.windows.length === 1 ? "" : "s"}` : "any time",
                  r.note ? `“${r.note}”` : null,
                ].filter(Boolean).join(" — ")}
                actions={
                  <Button disabled={busyId === r.id} onClick={() => claimRequest(r.id)} sx={tinyPrimary}>
                    Pick this up
                  </Button>
                }
              />
            ))}
          </Bay>

          {/* Picked up and still owing a time. Separate from the pool because
              the action is different and the obligation is now personal. */}
          <Bay label="You picked up — owe a time" count={pool.mine.length}>
            {pool.mine.map((r) => (
              <Strip
                key={r.id}
                kind="owed"
                when={`Lesson ${r.lesson}`}
                dur={r.claimExpiresAt ? `${moment(r.claimExpiresAt).fromNow(true)} left` : null}
                title={r.student}
                sub={[
                  r.lessonTitle,
                  r.windows?.length ? `free at ${r.windows.length} time${r.windows.length === 1 ? "" : "s"}` : "any time",
                ].filter(Boolean).join(" — ")}
                actions={
                  <>
                    <Button disabled={busyId === r.id} onClick={() => releaseRequest(r.id)} sx={tinySecondary}>
                      Put back
                    </Button>
                    <Button disabled={busyId === r.id} onClick={() => answerRequest(r)} sx={tinyPrimary}>
                      Propose a time
                    </Button>
                  </>
                }
              />
            ))}
          </Bay>

          <Bay label="Waiting on your answer" count={requests.length}>
            {requests.map((b) => (
              <Strip
                key={b._id}
                kind="requested"
                when={moment(b.start_time).format("ddd D MMM · HH:mm")}
                dur={`${Math.round((new Date(b.end_time) - new Date(b.start_time)) / 60000)}m`}
                meta={b.decision_deadline ? `expires ${moment(b.decision_deadline).fromNow(true)}` : null}
                title={b.studentName}
                sub={[
                  [b.subject, b.lesson ? `Lesson ${b.lesson}` : null].filter(Boolean).join(" · ") || null,
                  b.student_note ? `“${b.student_note}”` : null,
                ].filter(Boolean).join(" — ")}
                actions={
                  <>
                    <Button disabled={busyId === b._id} onClick={() => respond(b._id, "decline")} sx={tinySecondary}>
                      Decline
                    </Button>
                    <Button disabled={busyId === b._id} onClick={() => respond(b._id, "accept")} sx={tinyPrimary}>
                      Accept
                    </Button>
                  </>
                }
              />
            ))}
          </Bay>

          {/* No session exists yet, so there is nothing to draw on the diary —
              which is precisely why this belongs in the bay. */}
          <Bay label="Blocked on you" count={blocked.length}>
            {blocked.map((r) => {
              const why = r.reasons.find((x) => x.reason === "awaiting_session");
              return (
                <Strip
                  key={r.studentId}
                  kind="requested"
                  title={r.name}
                  sub={why?.detail || "Cannot start the next lesson until you sit with them"}
                  actions={
                    <Button
                      onClick={() => setPropose({ studentId: r.studentId, name: r.name, lesson: why?.lesson })}
                      sx={tinyPrimary}
                    >
                      Offer a time
                    </Button>
                  }
                />
              );
            })}
          </Bay>

          {/* CAPPED, AND THE CAP IS STATED. An instructor on the seeded US
              school has thirty-two of these; thirty-two strips is not a bay,
              it is a backlog, and it would push everything below it off the
              screen. The rest are not hidden — they are hatched on the
              calendar, which is where a backlog belongs. */}
          <Bay label="Debriefs owed" count={owed.length}>
            {owed.slice(0, MAX_OWED).map((b) => (
              <Strip
                key={b._id}
                kind="owed"
                when={moment(b.start_time).format("ddd D MMM")}
                dur={moment(b.start_time).fromNow()}
                title={b.studentName}
                // Only a session for a lesson gates anything, and what it locks
                // is the NEXT lesson. The fallback promised a lock that does
                // not exist for a UK group or an ad-hoc session — there, what is
                // owed is the follow-up work.
                sub={b.lesson
                  ? `Lesson ${b.lesson + 1} stays locked until you debrief this`
                  : "Their follow-up work goes out once you debrief this"}
                actions={<Button onClick={() => setPrep(b)} sx={filled(ACTION_COLORS.debrief)}>Debrief it</Button>}
              />
            ))}
            {owed.length > MAX_OWED && (
              <Typography sx={{ ...H, fontSize: "11px", color: textSecondary, pl: 0.3 }}>
                +{owed.length - MAX_OWED} older — hatched on the calendar
              </Typography>
            )}
          </Bay>

          <Bay label="Offered, awaiting reply" count={awaitingReply.length}>
            {awaitingReply.map((b) => (
              <Strip
                key={b._id}
                kind="proposed"
                when={moment(b.start_time).format("ddd D MMM · HH:mm")}
                meta={b.proposal_expires_at ? `lapses ${moment(b.proposal_expires_at).fromNow(true)}` : null}
                title={b.studentName}
                sub={b.proposal_note || "You offered this time — they have not answered yet"}
                actions={<Box sx={{ ml: "auto" }}><BookingActions booking={b} canTeach onDone={load} /></Box>}
              />
            ))}
          </Bay>

          <Bay label="Next up">
            {nextUp.map((e) => {
              const act = stripAction(e);
              const hint = joinHint(e);
              const open = () => openSession(e.klass ? e.klass.id : e.booking._id);
              return (
                <Strip
                  key={e.id}
                  kind={e.kind}
                  when={moment(e.start).calendar(null, {
                    sameDay: "[Today] HH:mm", nextDay: "[Tomorrow] HH:mm",
                    nextWeek: "ddd D MMM · HH:mm", sameElse: "ddd D MMM · HH:mm",
                  })}
                  dur={e.booking ? `${Math.round((e.end - e.start) / 60000)}m` : null}
                  meta={hint}
                  title={e.title}
                  sub={e.sub || (e.klass ? "Group class" : "1:1")}
                  onOpen={open}
                  actions={
                    <>
                      <Button
                        onClick={() => {
                          // Join is the one action that must NOT navigate: it
                          // is time-critical, and making somebody load a page
                          // to reach a call that is already running is the
                          // wrong trade every time.
                          if (act?.label === "Join now") openSessionRoom(e.booking._id);
                          else open();
                        }}
                        startIcon={act?.icon === "video" ? <VideocamIcon sx={{ fontSize: 14 }} />
                          : act?.icon === "class" ? <GroupsIcon sx={{ fontSize: 14 }} /> : null}
                        sx={act?.sx || tinySecondary}
                      >
                        {act?.label}
                      </Button>
                      {/* Reschedule, cancel and no-show stay in the overflow —
                          they are what you do to a session, not what you do
                          with it, and putting them beside the primary action
                          is how somebody cancels a lesson they meant to open. */}
                      {e.booking && <BookingActions booking={e.booking} canTeach onDone={load} />}
                    </>
                  }
                />
              );
            })}
          </Bay>

          {/* An empty bay is a real and good state, and should read as one
              rather than as a panel that failed to load. */}
          {nothingWaiting && !nextUp.length && (
            <Box sx={{ border: `1px dashed ${border}`, borderRadius: "10px", p: 2, textAlign: "center" }}>
              <Typography sx={{ ...H, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
                Nothing in the bay
              </Typography>
              <Typography sx={{ ...H, fontSize: "11.5px", color: textSecondary, mt: 0.4, lineHeight: 1.5 }}>
                No requests, nothing to debrief, nothing booked. If students cannot find a time with you,
                check your hours under Availability &amp; rates.
              </Typography>
            </Box>
          )}
          {nothingWaiting && nextUp.length > 0 && (
            <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted, mt: -1 }}>
              Nothing is waiting on you.
            </Typography>
          )}
        </Box>
      </Box>
      )}

      {/* ONE LEDGER: 1:1s and classes, upcoming and past. Upcoming rows are
          tinted and sit on top, soonest first; when a session finishes it
          stops being tinted and takes its place in the history below. Nothing
          moves between screens because there is only one.

          The class MANAGEMENT block stays — a table is a ledger, and seats,
          prices and calling a class off are not ledger operations. It is
          collapsed, and clicking a class row opens it. */}
      {teaches && tab === "sessions" && (
        <Box>
          <Sessions
            // The row shape is the library's and SessionPrep expects a
            // booking. `id` is the booking id, so this is a rename, not a guess.
            onDebrief={(row) => row && setPrep(asBooking(row))}
            onPrep={(row) => row && setPrep(asBooking(row))}
            // Every row, both kinds, to the same address. The one gesture
            // people use most on this table should not behave differently
            // depending on what they clicked.
            onOpen={(row) => openSession(row.id)}
            onOpenClass={(row) => openSession(row.id)}
            canSeeSchool={canManageAllClasses}
          />
          {groupClasses && (
          <Box ref={classesRef} sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${border}` }}>
            <Box
              onClick={() => setClassesOpen((v) => !v)}
              sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", userSelect: "none" }}
            >
              <ExpandMoreIcon sx={{
                fontSize: 20, color: textSecondary,
                transform: classesOpen ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s",
              }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...H, fontSize: "14px", fontWeight: 800, color: textPrimary }}>
                  Schedule &amp; manage classes
                </Typography>
                <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.15 }}>
                  Seats, prices, attendance and calling one off.
                </Typography>
              </Box>
            </Box>
            <Collapse in={classesOpen} mountOnEnter>
              <Box sx={{ mt: 2 }}>
                <GroupSessions embedded onlyInstructorId={canManageAllClasses ? null : myId} />
              </Box>
            </Collapse>
          </Box>
          )}
        </Box>
      )}

      {teaches && tab === "payouts" && (
        <Box>
          {/* WHAT YOU ARE PAID, before what you have been paid. An instructor
              looking at a payout figure cannot check it without knowing the
              arrangement behind it — a 70% share and a flat rate produce the
              same number from different sessions. Read-only: the school sets
              it, in Settings. */}
          {pay && (
            <Box sx={{ p: 1.75, border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#FAF8F5", mb: 2 }}>
              <Typography sx={{ ...bayLabel, mb: 1 }}>How you are paid</Typography>
              {[["1:1 sessions", pay.oneToOne], ["Group classes", pay.group]].map(([what, rule]) => (
                <Box key={what} sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.35, flexWrap: "wrap" }}>
                  <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, width: 110, flexShrink: 0 }}>{what}</Typography>
                  <Typography sx={{ ...H, fontSize: "13px", fontWeight: 700, color: textPrimary }}>
                    {describeRule(rule, currencySymbol(region))}
                  </Typography>
                  {rule?.source !== "instructor" && (
                    <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted }}>school default</Typography>
                  )}
                </Box>
              ))}
              <Typography sx={{ ...H, fontSize: "11.5px", color: textMuted, mt: 0.9 }}>
                Set by your school. Session prices are a different number — those are what the student pays.
              </Typography>
            </Box>
          )}

          {earnings?.paidToYouDirectly === false && (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textSecondary, mb: 2 }}>
              Your school is paid for these sessions and settles with you directly, so nothing here clears to your own account.
            </Typography>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
            {[
              { label: "Paid out", value: money(earnings?.totals?.paid || 0, cur) },
              { label: `Clearing (${earnings?.holdHours ?? 72}h hold)`, value: money(clearing, cur) },
              { label: "Due next run", value: money(earnings?.totals?.dueNextRun || 0, cur) },
              { label: "Failed", value: money(failedOut, cur), tone: failedOut ? errorColor : undefined },
            ].map((t) => (
              <Box key={t.label} sx={statTileSx}>
                <Typography sx={{ ...H, fontSize: "20px", fontWeight: 800, color: t.tone || textPrimary, fontVariantNumeric: "tabular-nums" }}>
                  {t.value}
                </Typography>
                <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mt: 0.25 }}>{t.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Failures name the reason. "Payout failed" with no cause is a
              message an instructor can only escalate, never act on. */}
          {(earnings?.needsAttention || []).length > 0 && (
            <Box sx={{ border: `1px solid ${errorColor}`, borderRadius: "10px", backgroundColor: `${errorColor}0d`, p: 1.5, mb: 2 }}>
              <Typography sx={{ ...bayLabel, color: errorColor, mb: 0.8 }}>Could not be paid out</Typography>
              {earnings.needsAttention.map((n) => (
                <Typography key={String(n.bookingId)} sx={{ ...H, fontSize: "12.5px", color: textPrimary, py: 0.2 }}>
                  {money(n.amount, cur)} — {moment(n.at).format("D MMM")} — {n.reason || "your payout account needs attention"}
                </Typography>
              ))}
            </Box>
          )}

          {!(earnings?.recent || []).length ? (
            <Typography sx={{ ...H, fontSize: "12.5px", color: textMuted }}>No payments yet.</Typography>
          ) : (
            <Box sx={{ border: `1px solid ${border}`, borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}>
              {(earnings.recent || []).map((r, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1.5, px: 1.75, py: 1.05,
                  borderBottom: `1px solid ${border}`, "&:last-of-type": { borderBottom: "none" },
                }}>
                  <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, width: 104, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                    {moment(r.at || r.sessionAt).format("D MMM YYYY")}
                  </Typography>
                  <Typography sx={{ ...H, fontSize: "12.5px", color: textPrimary, flex: 1, minWidth: 0 }}>
                    {r.studentName || "Session"}
                  </Typography>
                  <Typography sx={{ ...H, fontSize: "12.5px", fontWeight: 700, color: textPrimary, fontVariantNumeric: "tabular-nums" }}>
                    {money(r.amount, cur)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Drawn on the diary in purple as well. What lives here is the part a
          calendar cannot do: seats, price and calling one off. */}
      {/* A receptionist has no diary and no history — the classes ARE their
          page, so they get them directly rather than behind a disclosure. */}
      {!teaches && groupClasses && (
        <Box ref={classesRef}>
          <GroupSessions embedded onlyInstructorId={canManageAllClasses ? null : myId} />
        </Box>
      )}

      {teaches && tab === "profile" && (
        <Box ref={setupRef}>
          <Typography sx={{ ...H, fontSize: "12px", color: textSecondary, mb: 2 }}>
            Which days you work, what you cover, what you charge, and how you get paid.
            {profile && !profile.tutoring?.isBookable ? " You are currently closed to new bookings." : ""}
          </Typography>
          {/* Still mountOnEnter. Five forms, a Stripe status call and a
              students fetch have no business running for somebody who opened
              this page to see their Tuesday — which is now most opens, because
              Diary is the landing tab. */}
          <Collapse in={setupOpen} mountOnEnter>
            <InstructorTutorsView setupOnly />
          </Collapse>
        </Box>
      )}

      <SessionComposer
        open={Boolean(compose)}
        range={compose}
        consolidationRequest={answering}
        onClose={() => { setCompose(null); setAnswering(null); }}
        students={students}
        lessons={syllabus.lessons}
        defaultPricePerHead={syllabus.defaultPricePerHead}
        instructorId={myId}
        canScheduleClass={groupClasses && canManageAllClasses}
        currencySymbol={cur}
        onCreateSlot={createSlot}
        onDone={() => { setAnswering(null); load(); }}
        notify={(m, v) => enqueueSnackbar(m, { variant: v || "success" })}
      />

      {/* The panel. Same component as /school/sessions/:id, smaller frame. */}
      <SessionDetail
        id={peekId}
        onClose={() => setPeekId(null)}
        onExpand={(id) => { setPeekId(null); openSession(id); }}
        region={region}
        onJoin={(s) => { setPeekId(null); openSessionRoom(s.id, { group: s.kind === "class" }); }}
        onDebrief={(row) => { setPeekId(null); if (row) setPrep(asBooking(row)); }}
        onPrep={(row) => { setPeekId(null); if (row) setPrep(asBooking(row)); }}
      />

      <SessionPrep
        booking={prep}
        open={Boolean(prep)}
        onClose={() => setPrep(null)}
        onDone={load}
        onJoin={(b) => { setPrep(null); openSessionRoom(b._id); }}
        region={region}
      />
      <RecordActionPanel
        open={Boolean(propose)}
        mode="propose"
        studentId={propose?.studentId}
        record={propose ? { student: { name: propose.name }, weakest: [] } : null}
        prefill={propose ? {
          note: `You have finished Lesson ${propose.lesson || ""} and cannot start the next one until we go over it together. Half an hour should do it.`.replace("Lesson  ", "the lesson"),
        } : null}
        canTeach
        onClose={() => setPropose(null)}
        onDone={() => { setPropose(null); load(); }}
      />
    </Box>
  );
}
